import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControlLabel,
    Button,
    Snackbar,
    Alert,
    CssBaseline,
} from '@mui/material';
import { MenuBook as SubjectIcon } from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import { ThemeProvider } from '@mui/material/styles';

import adminTheme from '../../theme'; 
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import Lottie from 'lottie-react';
import animationData from '../../assets/icons/no-results.json';

const AssignSubjectsTable = () => {
    const schoolId = localStorage.getItem('schoolId');

    // --- State Variables ---
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Data States
    const [classesData, setClassesData] = useState([]);
    const [sectionsDataByClass, setSectionsDataByClass] = useState({});
    const [subjectsData, setSubjectsData] = useState([]);
    const [assignments, setAssignments] = useState([]); 

    // Track new assignments/unassignments per section
    const [sectionChanges, setSectionChanges] = useState({});
    const [actionLoading, setActionLoading] = useState(false);

    // --- Fetch Data ---
    const fetchAllData = useCallback(async () => {
        try {
            setLoading(true);

            // 1) Fetch active classes
            const classesRes = await axiosInstance.get(`/api/classes?schoolId=${schoolId}`);
            const activeClasses = classesRes.data.filter((cls) => cls.status === 'Active');

            // 2) Fetch active sections per class
            const sectionsByClassId = {};
            for (const cls of activeClasses) {
                const secRes = await axiosInstance.get(`/api/sections/class/${cls.classId}`);
                const activeSections = secRes.data.filter((sec) => sec.status === 'Active');
                sectionsByClassId[cls.classId] = activeSections;
            }

            // 3) Fetch active subjects
            const subjectsRes = await axiosInstance.get(`/api/subjects?schoolId=${schoolId}`);
            const allSubjects = subjectsRes.data.data || subjectsRes.data || [];
            const activeSubjects = allSubjects.filter((sub) => sub.status === 'Active');

            // 4) Fetch class-subject-assignments
            const assignmentsRes = await axiosInstance.get(`/api/class-subject-assignments/school/${schoolId}`);
            const fetchedAssignments = assignmentsRes.data;

            // Initialize sectionChanges state
            const initialSectionChanges = {};
            activeClasses.forEach((cls) => {
                const sections = sectionsByClassId[cls.classId] || [];
                sections.forEach((sec) => {
                    const key = `${cls.classId}_${sec.sectionId}`;
                    initialSectionChanges[key] = { add: [], remove: [] };
                });
            });

            // Set final states
            setClassesData(activeClasses);
            setSectionsDataByClass(sectionsByClassId);
            setSubjectsData(activeSubjects);
            setAssignments(fetchedAssignments);
            setSectionChanges(initialSectionChanges);
            setError('');
        } catch (err) {
            console.error('Error fetching data:', err);
            showSnackbar("Failed to fetch data. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        if (!schoolId) {
            setError('School ID not found. Please ensure you are logged in.');
            setLoading(false);
            return;
        }
        fetchAllData();
    }, [schoolId, fetchAllData]);

    // --- Helpers & Handlers ---
    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const isSubjectAssigned = (classId, sectionId, subjectId) => {
        const key = `${classId}_${sectionId}`;
        const currentChange = sectionChanges[key] || { add: [], remove: [] };

        const inDB = assignments.some((a) => a.classId === classId && a.sectionId === sectionId && a.subjectId === subjectId);
        const markedToAdd = currentChange.add.includes(subjectId);
        const markedToRemove = currentChange.remove.includes(subjectId);

        return (inDB && !markedToRemove) || markedToAdd;
    };

    const handleCheckboxChange = (classId, sectionId, subjectId, checked) => {
        const key = `${classId}_${sectionId}`;
        setSectionChanges((prev) => {
            const sectionChange = prev[key] || { add: [], remove: [] };
            const newAdd = [...sectionChange.add];
            const newRemove = [...sectionChange.remove];

            if (checked) {
                if (!newAdd.includes(subjectId)) newAdd.push(subjectId);
                const removeIndex = newRemove.indexOf(subjectId);
                if (removeIndex !== -1) newRemove.splice(removeIndex, 1);
            } else {
                if (!newRemove.includes(subjectId)) newRemove.push(subjectId);
                const addIndex = newAdd.indexOf(subjectId);
                if (addIndex !== -1) newAdd.splice(addIndex, 1);
            }

            return { ...prev, [key]: { add: newAdd, remove: newRemove } };
        });
    };

    const handleSaveSection = async (classId, sectionId) => {
        const key = `${classId}_${sectionId}`;
        const changes = sectionChanges[key] || { add: [], remove: [] };

        if (changes.add.length === 0 && changes.remove.length === 0) return;

        try {
            setActionLoading(true);

            // Add new assignments
            const addPromises = changes.add.map((subjectId) =>
                axiosInstance.post(`/api/class-subject-assignments`, {
                    classId, sectionId, subjectId, schoolId: Number(schoolId),
                })
            );

            // Remove existing assignments
            const removePromises = changes.remove.map(async (subjectId) => {
                const existingAssignment = assignments.find((a) => a.classId === classId && a.sectionId === sectionId && a.subjectId === subjectId);
                if (existingAssignment) {
                    return axiosInstance.delete(`/api/class-subject-assignments/${existingAssignment.assignmentId}`, { params: { schoolId } });
                }
                return null;
            });

            await Promise.all([...addPromises, ...removePromises]);
            await fetchAllData();

            setSectionChanges((prev) => ({ ...prev, [key]: { add: [], remove: [] } }));
            showSnackbar('Changes saved successfully.', 'success');

        } catch (err) {
            console.error('Error saving changes:', err);
            showSnackbar('Failed to save changes. Please try again.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // --- Empty State Data ---
    const noResultsMessage = useMemo(() => {
        return {
            title: "Ready to Assign Subjects",
            description: "You need at least one active Class and one active Subject to begin mapping. Please ensure your academic structure is set up first."
        };
    }, []);

    // --- Render ---
    return (
        <ThemeProvider theme={adminTheme}>
            <CssBaseline />

            <PageContainer sx={{ width: '100%' }}>
                <AdminPageHero
                    title="Class Subject Mapping"
                    subtitle="Assign subjects to each class section and save the academic structure in one place."
                    metrics={[
                        { label: 'Classes', value: classesData.length },
                        { label: 'Subjects', value: subjectsData.length },
                        { label: 'Sections', value: Object.values(sectionsDataByClass).reduce((acc, sections) => acc + (sections?.length || 0), 0) },
                    ]}
                />

                {error && (
                    <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2, mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Main Content Area */}
                {loading ? (
                    <AdminPanelCard title="Class Subject Mapping" subtitle="Loading classes, sections, subjects, and assignment state." sx={{ mt: 2 }}>
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="36vh" flexDirection="column" gap={1.2}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">Loading subject assignments...</Typography>
                        </Box>
                    </AdminPanelCard>
                ) : classesData.length === 0 || subjectsData.length === 0 ? (
                    
                    /* --- EMPTY STATE --- */
                    <AdminPanelCard sx={{ mt: 2 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                        <Box width={150} height={150} mb={2} opacity={0.8}>
                            {animationData ? (
                                <Lottie animationData={animationData} loop autoplay style={{ height: "100%", width: "100%" }} />
                            ) : (
                                <SubjectIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                            )}
                        </Box>
                        <Typography variant="h6" color="text.primary" fontWeight="bold" gutterBottom>
                            {noResultsMessage.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3} textAlign="center" maxWidth={450} lineHeight={1.6}>
                            {noResultsMessage.description}
                        </Typography>
                    </Box>
                    </AdminPanelCard>

                ) : (

                    /* --- TABLE VIEW --- */
                    <AdminPanelCard
                        title="Section Subject Assignments"
                        subtitle="Review each class section, select the relevant subjects, and save only the rows you changed."
                        sx={{ mt: 2 }}
                        contentSx={{ p: 0 }}
                    >
                    <TableContainer component={Box}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead sx={{ bgcolor: "#F5F7FB" }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '15%' }}>Class Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '20%' }}>Section</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '50%' }}>Assigned Subjects</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '15%' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {classesData.map((cls) => {
                                    const sections = sectionsDataByClass[cls.classId] || [];

                                    if (sections.length === 0) {
                                        return (
                                            <TableRow key={cls.classId}>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>{cls.className}</TableCell>
                                                <TableCell colSpan={3} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                    No active sections available for this class.
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }

                                    return sections.map((sec, index) => {
                                        const key = `${cls.classId}_${sec.sectionId}`;
                                        const hasChanges = sectionChanges[key]?.add.length > 0 || sectionChanges[key]?.remove.length > 0;

                                        return (
                                            <TableRow key={key} hover sx={{ '& td, & th': { borderBottom: '1px solid', borderColor: 'divider' }, '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                                                
                                                {/* Class Name spanning rows */}
                                                {index === 0 && (
                                                    <TableCell rowSpan={sections.length} sx={{ fontWeight: 700, bgcolor: '#F8FAFC', verticalAlign: 'top', pt: 2.3 }}>
                                                        {cls.className}
                                                    </TableCell>
                                                )}

                                                <TableCell sx={{ fontWeight: 500 }}>{sec.sectionName}</TableCell>
                                                
                                                <TableCell>
                                                    <Box display="flex" flexWrap="wrap" gap={1} py={0.5}>
                                                        {subjectsData.map((sub) => {
                                                            const isChecked = isSubjectAssigned(cls.classId, sec.sectionId, sub.subjectId);
                                                            return (
                                                                <FormControlLabel
                                                                key={sub.subjectId}
                                                                sx={{ 
                                                                        margin: 0, 
                                                                        padding: '2px 12px 2px 4px', // Chip-like padding
                                                                        borderRadius: 2,
                                                                        border: '1px solid',
                                                                        borderColor: isChecked ? 'primary.main' : 'divider',
                                                                        bgcolor: isChecked ? 'primary.light' : 'background.paper',
                                                                        transition: 'all 0.2s',
                                                                        '&:hover': { bgcolor: isChecked ? 'primary.light' : 'action.hover' }
                                                                    }}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={isChecked}
                                                                            onChange={(e) => handleCheckboxChange(cls.classId, sec.sectionId, sub.subjectId, e.target.checked)}
                                                                            size="small"
                                                                            color="primary"
                                                                            disabled={actionLoading}
                                                                            sx={{ p: 0.5, mr: 0.5 }} // Tighten gap between checkbox and label inside chip
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Typography variant="body2" sx={{ fontWeight: isChecked ? 600 : 500, color: isChecked ? 'primary.dark' : 'text.primary' }}>
                                                                            {sub.subjectName}
                                                                        </Typography>
                                                                    }
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </TableCell>
                                                
                                                <TableCell align="right">
                                                    <Button
                                                        variant={hasChanges ? "contained" : "outlined"}
                                                        color={hasChanges ? "primary" : "inherit"}
                                                        size="small"
                                                        disableElevation
                                                        onClick={() => handleSaveSection(cls.classId, sec.sectionId)}
                                                        disabled={!hasChanges || actionLoading}
                                                        sx={{ minWidth: 80, textTransform: 'none', fontWeight: 600 }}
                                                    >
                                                        {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                                                    </Button>
                                                </TableCell>

                                            </TableRow>
                                        );
                                    });
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </AdminPanelCard>
                )}

                {/* --- Global Snackbar --- */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={(e, reason) => { if (reason !== 'clickaway') setSnackbar(prev => ({ ...prev, open: false })) }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%', color: '#fff', fontWeight: 500, borderRadius: 2 }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </PageContainer>
        </ThemeProvider>
    );
};

export default AssignSubjectsTable;
